import classNames from "classnames";
import parse from "html-react-parser";
import { t } from "i18next";
import React, { useEffect, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import uuid from "react-uuid";
import "simplebar-react/dist/simplebar.min.css";

import useCheckDevice from "@repo/shopifyV2/hooks/useCheckDevice";
import useTimer from "@repo/shopifyV2/hooks/useTimer";
import useTracking from "@repo/shopifyV2/hooks/useTracking";
import type { IProductDetailNode, IProductDetailQuery } from "@repo/shopifyV2/interface/product";
import type {
  Collection,
  MediaImage as MediaImageType,
  Image as ShopifyImage,
  Video as VideoType,
} from "@repo/shopifyV2/types/storefront.types";
import { MediaContentType } from "@repo/shopifyV2/types/storefront.types";
import { extractId } from "@repo/shopifyV2/utils/common";
import {
  checkProductLimitReached,
  checkProductLimitReachedCaa,
  checkVariantLimitReached,
  checkVariantLimitReachedCaa,
} from "@repo/shopifyV2/utils/product";

import Button from "@atoms/Button";
import Image from "@atoms/Image";
import Link from "@atoms/Link";
import Price from "@atoms/Price";
import Select from "@atoms/Select";
import SizeChart from "@atoms/SizeChart";
import Table from "@atoms/Table";
import Video from "@atoms/Video";

import ColorSelector from "@molecules/ColorSelector";
import type { IRestrictedMediaEdge } from "@molecules/MediaSlider";
import MediaSlider from "@molecules/MediaSlider";
import RestockNotification from "@molecules/RestockNotification";

import SizeRecommender from "@organisms/SizeRecommender";
import ZoomImageSlider from "@organisms/ZoomImageSlider";

import DanielJohnstonButton from "@pages/DanielJohnston/Button";
import DanielJohnstonSelect from "@pages/DanielJohnston/Select";

import { NOTIFICATIONS } from "@const/notifications";
import { isSizeRecommendationSupported } from "@const/sizeRecommender";
import { URLS } from "@const/urls";

import useCheckLocation from "@hooks/useCheckLocation";
import useLanguage from "@hooks/useLanguage";

import useCartQuery from "@queries/useCart";

import { useCartStore } from "@store/cart";
import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

interface IProps {
  product: IProductDetailQuery["product"];
}

const RECOMMEND_SIZE_OPTION_VALUE = "recommend-size";

export default function ProductInformation({ product }: IProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const { isPC } = useCheckDevice();

  const { openToast, openCart, openModal } = useLayoutStore();
  const { cart, isSyncing } = useCartStore();
  const { customer, caaCustomer } = useCustomerStore();

  const { gtmAddToCart, gtmViewItem } = useTracking();

  const { isDanielJohnston } = useCheckLocation();

  const { addCartLine, addCartLineLoading } = useCartQuery();
  const { lang } = useLanguage();

  const variantId = searchParams.get("variantId") || "";

  const [enlargedIndex, setEnlargedIndex] = useState(0);
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedTab, setSelectedTab] = useState<"detail" | "size" | "shipping">("detail");
  const [visibleImages, setVisibleImages] = useState(-1);
  const [sizeType, setSizeType] = useState(lang === "en" ? "Inch" : "CM");
  const [isEnlarged, setIsEnlarged] = useState(false);
  const [available, setAvailable] = useState(true);

  const releaseDate = useMemo(() => (product?.release_date ? new Date(product?.release_date?.value) : null), [product]);
  const { watch, formattedTimer } = useTimer({ dueDate: releaseDate ? releaseDate : new Date(), type: "increase" });
  const isUpcoming = Boolean(releaseDate && releaseDate > watch);

  useEffect(() => {
    if (!product) return;

    const findProduct = product.variants.edges.find(({ node }) => extractId(node.id) === (variantId || extractId(node.id)));
    const findColorOption = findProduct?.node.selectedOptions.find((option) => option.name === "Color");
    setSelectedColor(findColorOption?.value || "");

    const availableOptions = product.variants.edges.filter(
      ({ node }) => node.selectedOptions.find((item) => item.name === "Color")?.value === findColorOption?.value && node.availableForSale,
    );

    if (availableOptions?.length > 0) {
      setSelectedSize(extractId(availableOptions[0]?.node.id || findProduct?.node.id || product.variants.edges[0].node.id));
    } else {
      setSelectedSize(extractId(findProduct?.node.id || product.variants.edges[0].node.id));
    }
  }, [product, variantId]);

  const isSizeSupported = useMemo(() => isSizeRecommendationSupported(product?.productType ?? ""), [product]);

  useEffect(() => {
    if (product) gtmViewItem(product, extractId(customer?.id || ""));
  }, [product, customer]);

  const variantList = useMemo(
    () =>
      product?.variants?.edges.filter(({ node }) => node.selectedOptions.find((item) => item.name === "Color")?.value === selectedColor) ||
      [],
    [selectedColor, product],
  );

  const isGift = useMemo(() => product?.vendor.includes("GWP"), [product]);

  const selectedVariant = useMemo(() => variantList.find(({ node }) => extractId(node.id) === selectedSize), [variantList, selectedSize]);

  const colorList = useMemo(() => {
    const list: IProductDetailNode[] = [];

    const colorOption = product?.options.find((item) => item.name === "Color")?.values || [];

    colorOption.forEach((colorItem) => {
      product?.variants?.edges.forEach(({ node }) => {
        const optionColor = node.selectedOptions.find((option) => option.value === colorItem);

        if (optionColor) {
          const findList = list.find((item) => item.selectedOptions.find((option) => option.value === colorItem)?.value === colorItem);
          if (!findList) list.push(node);
        }
      });
    });

    return list;
  }, [product]);

  const handleSelectColor = (item: IProductDetailNode) => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setSearchParams({ variantId: extractId(item.id) }, { replace: true });
  };

  const handleSizeSelect = (value: string) => {
    if (value === RECOMMEND_SIZE_OPTION_VALUE) {
      if (lang === "en") {
        setIsSizeGuideOpen(true);
      }
      return;
    }
    setSelectedSize(value);
  };

  // 이 상품이 실제로 갖고 있는 사이즈 옵션 값들. SizeRecommender가 추천값을 보정하는 데 사용
  const availableSizeValues = useMemo(() => {
    const values = new Set<string>();
    variantList.forEach(({ node }) => {
      const size = node.selectedOptions.find((item) => item.name === "Size")?.value;
      if (size) values.add(size);
    });
    return Array.from(values);
  }, [variantList]);

  // Size Recommender에서 추천 사이즈를 Apply하면, 같은 Size 값을 가진 variant를 선택
  const handleApplyRecommendedSize = (size: string) => {
    const findVariant = variantList.find(({ node }) => node.selectedOptions.find((item) => item.name === "Size")?.value === size);
    if (!findVariant) return;

    setSelectedSize(extractId(findVariant.node.id));
  };

  const sizeList = useMemo(() => {
    const list: { name: string; value: number }[] = [];

    variantList.forEach(({ node }) => {
      const size = node.selectedOptions.find((item) => item.name === "Size")?.value;
      if (size) {
        list.push({
          name: node.availableForSale ? size : `${size} - ${lang === "jp" ? "再入荷通知" : "Out of Stock"}`,
          value: parseInt(extractId(node.id)),
        });
      }
    });

    return list;
  }, [variantList, lang]);

  // 사이즈 select 옵션에 "Size Recommender"를 실제 사이즈들과 구분되는 spacer와 함께 덧붙임
  const sizeSelectOptions = useMemo((): { name: string; value: number | string }[] => {
    if (!isSizeSupported || !sizeList.length || lang !== "en") return sizeList;

    return [...sizeList, { name: " ", value: "" }, { name: "Size Recommender", value: RECOMMEND_SIZE_OPTION_VALUE }];
  }, [sizeList, isSizeSupported]);

  const handleAddCart = async () => {
    // 로그인 직후 카트 병합 중에 담으면 곧 버려질 게스트 카트에 담기므로 대기
    if (isSyncing) return;

    const findProduct = variantList.find(({ node }) => extractId(node.id) === selectedSize);

    if (!selectedSize || !selectedColor || !findProduct) {
      openToast(NOTIFICATIONS.ERROR_SELECT_OPTION);
      return;
    }

    const cartData = await addCartLine({ cartId: cart.id, lines: [{ merchandiseId: findProduct.node.id, quantity: 1 }] });
    const cartSelectedOption = cartData.cartLinesAdd.cart.lines.edges?.find(({ node }) => node.merchandise.id === findProduct.node.id);
    if (cartSelectedOption && cartSelectedOption.node.quantity <= 0) {
      openToast(NOTIFICATIONS.WARNING_NO_QUANTITY);
      return;
    }

    if (!cartData?.cartLinesAdd.userErrors.length) {
      gtmAddToCart(findProduct.node, extractId(customer?.id || ""), customer?.email || undefined);
      openCart(true);
    }
  };

  const sizeChart = useMemo(() => {
    if (!product?.size_fit) return { titles: [], data: [] };

    const sizeOption = product.options.filter((option) => option.name === "Size").pop()?.values ?? [];

    const sizeFit = JSON.parse(product.size_fit.value || "[]") as string[];
    const data = sizeFit
      .slice(1) // 첫번째 값은 제거
      .map((val: string) => val.split("/"))
      .filter((item) => item.length);

    return { titles: ["Size", ...sizeOption], data: data.map((trs) => trs), type: sizeFit[0] };
  }, [product]);

  const sizeChartInch = useMemo(() => {
    const regex = /^-?\d+(?<decimal>\.\d+)?$/; // 숫자만 체크
    return sizeChart.data.map((row) => {
      return row.map((sizeData) => {
        if (!regex.test(sizeData)) {
          return sizeData;
        }
        const inchSize = (parseInt(sizeData) * 0.393701).toFixed(1);
        return inchSize.endsWith(".0") ? inchSize.split(".")[0] : inchSize;
      });
    });
  }, [sizeChart]);

  // 각 variant에 해당하는 media를 모아둔 리스트
  const mediaByVariantList = useMemo(() => {
    if (product === undefined) return [];

    // { key: variant 아이디, value: variant 메인 이미지 URL } 형태의 객체 생성
    const imageUrlByVariant: Record<string, string> = {};
    for (const variant of product.variants.edges) {
      const nodeId = variant.node.id;
      const nodeImageUrl = variant.node.image?.url || "";

      // 이미지 URL이 이미 존재하는 경우는 건너뜀
      if (Object.values(imageUrlByVariant).includes(nodeImageUrl)) continue;

      imageUrlByVariant[nodeId] = nodeImageUrl;
    }

    // imageUrlByVariant의 이미지 URL을 기준으로 media 데이터에서 variant의 인덱스를 찾음
    const mediaIndexByVariant: Record<string, number | string>[] = [];
    for (const [key, imageUrl] of Object.entries(imageUrlByVariant)) {
      const findIndex =
        product?.media.edges.findIndex(({ node }) => {
          if (node.mediaContentType === MediaContentType.Image) return (node as MediaImageType).image?.url === imageUrl;
          if (node.mediaContentType === MediaContentType.Video) return node.previewImage?.url === imageUrl;
          return false;
        }) ?? -1;

      // 해당 이미지 URL이 media에 존재하지 않는 경우는 건너뜀
      if (findIndex === -1) continue;

      mediaIndexByVariant.push({ id: key, src: imageUrl, mediaIndex: findIndex });
    }

    // 결과: { id: variant 아이디, media: IRestrictedMediaEdge[] 타입 리스트 } 형태의 배열
    return mediaIndexByVariant
      .filter((variant) => {
        return product?.variants.edges.find(({ node }) => node.image?.url === variant.src);
      })
      .map((variant, index) => {
        const { id, mediaIndex } = variant;
        const isLastVariant = index === mediaIndexByVariant.length - 1;

        return {
          id,
          media: product?.media.edges
            .slice(mediaIndex as number, isLastVariant ? undefined : (mediaIndexByVariant[index + 1].mediaIndex as number))
            // mediaContentType가 IMAGE 또는 VIDEO인 경우만 필터링하고 타입을 IRestrictedMediaEdge로 캐스팅
            .map((media) => media as IRestrictedMediaEdge),
        };
      });
  }, [product]);

  // 현재 선택된 variant에 해당하는 media를 가져옴
  const currentVariantMedia = useMemo<IRestrictedMediaEdge[]>(
    () => mediaByVariantList.find((item) => item.id === variantList[0]?.node.id)?.media || [],
    [mediaByVariantList, variantList],
  );

  const thumbnailImages = useMemo(() => {
    if (!isPC || !currentVariantMedia.length) return [];

    return currentVariantMedia
      .map(({ node }, mediaIndex) => {
        const image = node.mediaContentType === MediaContentType.Image ? (node as MediaImageType).image : (node as VideoType).previewImage;

        return { image, mediaIndex };
      })
      .filter((item): item is { image: ShopifyImage; mediaIndex: number } => Boolean(item.image));
  }, [currentVariantMedia, isPC]);

  useEffect(() => {
    if (!currentVariantMedia.length) return;

    checkImageDotPosition();

    window.addEventListener("scroll", checkImageDotPosition);

    return () => {
      window.removeEventListener("scroll", checkImageDotPosition);
    };
  }, [currentVariantMedia]);

  // 상세 페이지 목록의 점만 스크롤로 판단한다.
  // 확대 뷰는 Swiper 라 activeIndex 로 직접 알 수 있다.
  const checkImageDotPosition = () => {
    const center = window.innerHeight / 2;

    const centeredIndex = Array.from(document.querySelectorAll('[id^="media-"]')).findIndex((element) => {
      const { top, bottom } = element.getBoundingClientRect();
      return top <= center && bottom > center;
    });

    if (centeredIndex !== -1) setVisibleImages(centeredIndex);
  };

  useEffect(() => {
    if (!selectedVariant) return;

    // 수량 제한 체크
    const isProductLimitReached =
      lang === "jp"
        ? checkProductLimitReachedCaa({ product, cart, customer: caaCustomer })
        : checkProductLimitReached({ product, cart, customer });
    const isVariantLimitReached =
      lang === "jp"
        ? checkVariantLimitReachedCaa({ product, cart, customer: caaCustomer, selectedOption: selectedVariant.node.id })
        : checkVariantLimitReached({ product, cart, customer, selectedOption: selectedVariant.node.id });

    // 상품 구매 수량 제한 및 variant 구매 수량 제한
    if (isProductLimitReached || isVariantLimitReached) {
      setAvailable(false);
      return;
    }

    // 현재 고른 variant가 카트에 이미 담겨있는지 확인하고 이미 담겨있다면 구매 가능한 재고와 이미 담겨있는 재고를 비교
    const variantLine = cart.lines.edges.find(({ node }) => selectedVariant.node.id === node.merchandise.id)?.node;
    if (variantLine && variantLine.quantity >= (variantLine.merchandise.quantityAvailable ?? 0)) {
      setAvailable(false);
      return;
    }

    setAvailable(true);
  }, [cart, selectedVariant, customer, caaCustomer, lang, product]);

  const openEnlarged = (index: number) => {
    // Swiper 의 initialSlide 로 넘겨서 연다. 위치 계산이 따로 필요 없다
    setEnlargedIndex(index);
    setIsEnlarged(true);
  };

  const modelInformation = useMemo(() => {
    const cmToFeetInches = (cm: number) => {
      const cmPerInch = 2.54;
      const inchesPerFoot = 12;

      // Convert cm to inches
      const totalInches = cm / cmPerInch;

      // Calculate feet and remaining inches
      const feet = Math.floor(totalInches / inchesPerFoot);
      const inches = Math.round(totalInches % inchesPerFoot);

      return `${feet}'${inches}"`;
    };

    if (!product) return;

    const maleModel = JSON.parse(product?.maleModel?.value || "[]") as string[];
    const femaleModel = JSON.parse(product?.femaleModel?.value || "[]") as string[];

    return {
      male: {
        cm: maleModel[0],
        feet: cmToFeetInches(Number(maleModel[0])),
        size: maleModel[1],
      },
      female: {
        cm: femaleModel[0],
        feet: cmToFeetInches(Number(femaleModel[0])),
        size: femaleModel[1],
      },
    };
  }, [product?.maleModel, product?.femaleModel]);

  const notice = useMemo(() => JSON.parse(product?.notice?.value ?? "[]") as string[], [product]);

  const isJpRestockAvailable = lang === "jp" && !isUpcoming && Boolean(selectedSize) && !selectedVariant?.node?.availableForSale;

  const addToCartLabel = () => {
    if (isJpRestockAvailable) return "メールで通知を受け取る";
    if (!selectedSize || selectedVariant?.node?.availableForSale) return "Add to Cart";
    return "Out of Stock";
  };

  const danielJohnstonAddToCartLabel = () => {
    if (isUpcoming) return "Upcoming Item";
    if (!selectedSize || selectedVariant?.node?.availableForSale) return "Add to Cart";
    return "Out of Stock";
  };

  const handleOpenRestockModal = () => {
    if (!selectedVariant) return;

    openModal({
      title: "Restock Notification",
      content: <RestockNotification defaultVariantId={selectedVariant.node.id} variantList={variantList} />,
      isHideCancelButton: true,
    });
  };

  return (
    <section
      className={classNames(
        "tnt-gap-x pc:col-span-12 pc:grid-cols-12 relative col-span-8 grid h-full grid-cols-8",
        isDanielJohnston && "bg-dj-black",
      )}
    >
      <div className="pc:sticky pc:w-screen-col-span-5 pc:left-5 pc:col-span-3 pc:row-start-1 pc:h-[calc(100vh-150px)] top-25 left-0 col-span-8 row-start-3 flex flex-col justify-between gap-5 self-start">
        <div className="pc:gap-15 flex w-full flex-col gap-10">
          <section className="pc:gap-5 flex flex-col gap-4">
            <div className="pc:gap-2.5 flex flex-col gap-2">
              <h1 className={classNames("text-title flex flex-col gap-1 leading-none", isDanielJohnston && "font-daily text-2xl")}>
                <span>{product?.title || ""}</span>
                <span className="font-normal">{selectedColor}</span>
              </h1>
              <Price
                className={classNames("text-title leading-noneㅋ", isDanielJohnston && "font-daily text-2xl")}
                compareAtPrice={parseFloat(variantList[0]?.node.compareAtPrice?.amount || "0")}
                price={parseFloat(variantList[0]?.node.price?.amount || "0")}
              />
            </div>

            <div className="pc:w-screen-col-span-2 pc:gap-1 flex w-full flex-col gap-2">
              <div className="pc:gap-1 flex flex-row gap-2">
                {colorList?.map((item) => (
                  <ColorSelector
                    className="pc:size-7.25 size-8.5 sm:size-7.5 md:size-7.75 lg:size-8"
                    key={item.id}
                    onClick={() => {
                      handleSelectColor(item);
                    }}
                    position={item.product.tags.includes("Thumbnail Bottom") ? "bottom" : "center"}
                    src={item.image?.url || ""}
                  />
                ))}
              </div>

              {/* 사이즈 선택 / 장바구니 추가 */}
              {!isGift && (
                <>
                  {!isUpcoming && sizeList.length ? (
                    <>
                      {isDanielJohnston ? (
                        <DanielJohnstonSelect
                          onChange={(e) => {
                            handleSizeSelect(e.target.value);
                          }}
                          options={sizeSelectOptions}
                          value={selectedSize}
                        />
                      ) : (
                        <Select
                          as="square"
                          className="truncate"
                          onChange={(e) => {
                            handleSizeSelect(e.target.value);
                          }}
                          options={sizeSelectOptions}
                          value={selectedSize}
                        />
                      )}
                    </>
                  ) : null}
                  {isDanielJohnston ? (
                    <DanielJohnstonButton
                      disabled={(selectedSize ? !selectedVariant?.node?.availableForSale : false) || !available || isUpcoming}
                      isLoading={addCartLineLoading || isSyncing}
                      onClick={handleAddCart}
                    >
                      {danielJohnstonAddToCartLabel()}
                    </DanielJohnstonButton>
                  ) : (
                    <Button
                      as={isUpcoming ? "notice" : "square"}
                      className="w-full justify-start!"
                      disabled={
                        isJpRestockAvailable
                          ? false
                          : (selectedSize ? !selectedVariant?.node?.availableForSale : false) || !available || isUpcoming
                      }
                      loading={addCartLineLoading || isSyncing}
                      onClick={isJpRestockAvailable ? handleOpenRestockModal : handleAddCart}
                    >
                      {isUpcoming ? (
                        <>
                          <span>Upcoming Item</span>
                          <span className="font-normal">
                            {`${formattedTimer.days}d : ${formattedTimer.hours}h : ${formattedTimer.minutes}m : ${formattedTimer.seconds}s`}
                          </span>
                        </>
                      ) : (
                        <>{addToCartLabel()}</>
                      )}
                    </Button>
                  )}
                </>
              )}
              {!available ? <div className="text-red">{t("warning.quantity_limit")}</div> : null}
            </div>
          </section>

          {/* Upcoming */}
          {isDanielJohnston && isUpcoming ? (
            <section className="font-fuzzy flex flex-col gap-1">
              <span>Upcoming Item</span>
              <span>
                {`This Item will be available in ${formattedTimer.days}d : ${formattedTimer.hours}h : ${formattedTimer.minutes}m : ${formattedTimer.seconds}s`}
              </span>
            </section>
          ) : null}

          {notice.length || product?.tags.includes("Pigment") ? (
            <section className={classNames("flex flex-col gap-2", isDanielJohnston && "font-fuzzy font-fuzzy-bold")}>
              <span>Note</span>
              <ul className="font-normal">
                {notice?.map((item) => (
                  <li key={uuid()}>{item}</li>
                ))}
                {product?.tags.includes("Pigment") ? <li>{t("product.pigment_warning")}</li> : null}
              </ul>
            </section>
          ) : null}

          {/* 상품 상세 정보 */}
          <section className={classNames("flex flex-col gap-2", isDanielJohnston && "font-fuzzy font-fuzzy-bold")}>
            <div className="pc:gap-3 flex flex-row gap-4">
              <Button
                as="text"
                onClick={() => {
                  setSelectedTab("detail");
                }}
                variant={selectedTab === "detail" ? "primary" : "secondary"}
              >
                Details
              </Button>
              {sizeChart.data.length ? (
                <Button
                  as="text"
                  onClick={() => {
                    setSelectedTab("size");
                  }}
                  variant={selectedTab === "size" ? "primary" : "secondary"}
                >
                  Size Guide
                </Button>
              ) : null}

              <Button
                as="text"
                onClick={() => {
                  setSelectedTab("shipping");
                }}
                variant={selectedTab === "shipping" ? "primary" : "secondary"}
              >
                Shipping and Return
              </Button>
            </div>

            <div className="tnt-gap-x pc:grid-cols-4 grid grid-cols-8 gap-y-5">
              {/* 상품 설명 */}
              {selectedTab === "detail" && (
                <div className={classNames("pc:col-span-4 col-span-8 font-normal", isDanielJohnston && "font-fuzzy font-fuzzy-light!")}>
                  {parse(product?.descriptionHtml || "")}
                </div>
              )}

              {/* 사이즈 차트 */}
              {selectedTab === "size" && (
                <>
                  <div className="pc:col-span-8 col-span-4">
                    {modelInformation?.male.cm || modelInformation?.female.cm ? (
                      <ul className="mb-5 font-normal">
                        {modelInformation?.male.cm ? (
                          <>
                            <li>Male Model</li>
                            <li className="list-dash">
                              {modelInformation?.male.cm}cm({modelInformation?.male.feet}), wearing size {modelInformation.male.size}
                            </li>
                          </>
                        ) : null}
                        {modelInformation?.female.cm ? (
                          <>
                            <li>Female Model</li>
                            <li className="list-dash">
                              {modelInformation?.female.cm}cm({modelInformation?.female.feet}), wearing size {modelInformation.female.size}
                            </li>
                          </>
                        ) : null}
                      </ul>
                    ) : null}
                    <Table data={sizeType === "CM" ? sizeChart.data : sizeChartInch} titles={sizeChart.titles} />
                    <div className={classNames("mt-1 flex gap-2.5", lang === "en" && "flex-row-reverse justify-end")}>
                      <Button
                        className={classNames(sizeType !== "CM" && "text-grey")}
                        onClick={() => {
                          setSizeType("CM");
                        }}
                      >
                        CM
                      </Button>
                      /
                      <Button
                        className={classNames(sizeType !== "Inch" && "text-grey")}
                        onClick={() => {
                          setSizeType("Inch");
                        }}
                      >
                        INCH
                      </Button>
                    </div>
                  </div>
                  <div className="pc:col-span-4 col-span-8">
                    <SizeChart className="h-64 w-auto" type={sizeChart.type} />
                  </div>
                </>
              )}
              {/* 배송 및 반품 정보 */}
              {selectedTab === "shipping" && (
                <p className="pc:col-span-4 col-span-8 flex flex-col gap-2.5 font-normal">
                  <Trans
                    components={{ information: <Link as="text" href={URLS.SHIPPING_AND_RETURNS} /> }}
                    i18nKey="product.shipping.description"
                    values={{ domain: `${lang === "jp" ? "thisisneverthat.jp" : "thisisneverthat.com"}${URLS.SHIPPING_AND_RETURNS}` }}
                  />
                </p>
              )}
            </div>
          </section>

          {/* 추천 컬렉션 */}
          {product?.related_collection?.references?.edges ? (
            <section className={classNames("flex flex-col gap-2", isDanielJohnston && "font-fuzzy font-fuzzy-bold")}>
              <span>Explore more Items from</span>
              <div className="pc:gap-1 flex flex-col gap-2">
                {product?.related_collection?.references?.edges?.map(({ node }) => (
                  <Link
                    as="text"
                    href={`${URLS.COLLECTIONS_DETAIL}/${(node as Collection).handle}`}
                    isActive={false}
                    key={(node as Collection).id}
                  >
                    {(node as Collection).title}
                  </Link>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </div>

      {/* 이미지 리스트 */}
      <div className="pc:row-start-1 pc:col-start-6 pc:w-screen-col-span-6 pc:col-span-6 col-span-8 row-start-2 flex flex-col gap-0.5">
        {isEnlarged ? (
          <ZoomImageSlider
            initialSlide={enlargedIndex}
            media={currentVariantMedia}
            onClose={() => {
              setIsEnlarged(false);
            }}
            thumbnails={thumbnailImages}
          />
        ) : null}

        {isPC ? (
          <>
            {currentVariantMedia.map(({ node }, index) => (
              <Button
                as="wrapper"
                className={classNames("w-full", isEnlarged ? "cursor-zoom-out" : "cursor-zoom-in")}
                data-index={index}
                key={node.id}
                onClick={() => {
                  openEnlarged(index);
                }}
              >
                <>
                  {node.mediaContentType === MediaContentType.Image ? (
                    <Image
                      alt={(node as MediaImageType).image?.altText || ""}
                      height={(node as MediaImageType).image?.height || 1707}
                      id={`media-${index}`}
                      src={(node as MediaImageType).image?.url || ""}
                      width={(node as MediaImageType).image?.width || 1280}
                    />
                  ) : (
                    <Video
                      className="object-cover"
                      height={(node as VideoType).previewImage?.height || 1707}
                      id={`media-${index}`}
                      poster={(node as VideoType).previewImage?.url || ""}
                      src={(node as VideoType)?.sources?.[1]?.url || ""}
                      width={(node as VideoType).previewImage?.width || 1280}
                    />
                  )}
                </>
              </Button>
            ))}
          </>
        ) : (
          <MediaSlider media={currentVariantMedia} onEnlarge={openEnlarged} />
        )}
      </div>

      {/* 데스크탑 이미지 썸네일 */}
      {isPC ? (
        <section className="scrollbar-hide sticky top-25 col-start-12 flex h-fit max-h-[calc(100vh-260px)] w-[50%] max-w-12.5 min-w-10 flex-col items-end gap-1 justify-self-end overflow-x-scroll overflow-y-scroll">
          {thumbnailImages.map(({ image, mediaIndex }) => (
            <Button
              as="wrapper"
              className="relative"
              key={image.id}
              onClick={() => {
                const imageElement = document.getElementById(`media-${mediaIndex}`);
                imageElement?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              <Image
                alt={image?.altText || ""}
                height={image?.height || 1707}
                src={image?.url.replaceAll(".jpg", "_128x.jpg") || ""}
                width={image?.width || 1280}
              />
              {visibleImages === mediaIndex && <div className="absolute top-0 left-0 size-full bg-black/10" />}
            </Button>
          ))}
        </section>
      ) : null}

      {lang === "en" && isSizeGuideOpen && isSizeSupported ? (
        <SizeRecommender
          availableSizes={availableSizeValues}
          gender={customer?.gender?.value ?? null}
          height={customer?.height?.value ?? null}
          onApply={handleApplyRecommendedSize}
          onClose={() => {
            setIsSizeGuideOpen(false);
          }}
          productType={product?.productType}
          weight={customer?.weight?.value ?? null}
        />
      ) : null}
    </section>
  );
}
